/* =============================================================
   useStudentProcess.js — Student process step state
   -------------------------------------------------------------
   Fetches and updates the current process step for a single
   student from the student_process table.

   Cache strategy (module-level Map):
     _processCache survives React component unmount/remount and
     Supabase token refreshes. On every mount the hook returns
     cached data immediately, then silently re-fetches to stay
     in sync with the DB.

   Usage:
     const { currentStep, processLoading, advance, revert }
       = useStudentProcess(studentId);

   Behaviour:
     - On first load: reads student_process for this student.
       If no row exists yet, currentStep defaults to 0.
     - advance(maxStep): moves forward one step (up to maxStep).
     - revert():         moves back one step (minimum 0).
     - Both write to Supabase immediately (upsert) and update
       the module-level cache so re-navigating shows the
       latest step instantly.
   ============================================================= */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';


/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
// Key: studentId → { currentStep: number, rowId: string|null }
const _processCache = new Map();


export function useStudentProcess(studentId) {
  const _cached = studentId ? _processCache.get(studentId) : undefined;

  // Initialize from cache immediately — no spinner on re-navigation
  const [currentStep,    setCurrentStep]    = useState(_cached?.currentStep ?? 0);
  const [rowId,          setRowId]          = useState(_cached?.rowId       ?? null);
  const [processLoading, setProcessLoading] = useState(_cached === undefined);


  /* ── fetch ─────────────────────────────────────────────────── */
  const fetchProcess = useCallback(async () => {
    if (!studentId) { setProcessLoading(false); return; }

    // Show spinner only on the very first fetch for this student
    if (!_processCache.has(studentId)) setProcessLoading(true);

    const { data } = await supabase
      .from('student_process')
      .select('id, current_step')
      .eq('student_id', studentId)
      .maybeSingle();

    if (data) {
      setCurrentStep(data.current_step);
      setRowId(data.id);
      _processCache.set(studentId, { currentStep: data.current_step, rowId: data.id });
    } else {
      // No row yet — keep default 0 but record that we checked
      _processCache.set(studentId, { currentStep: 0, rowId: null });
    }

    setProcessLoading(false);
  }, [studentId]);

  useEffect(() => { fetchProcess(); }, [fetchProcess]);


  /* ── saveStep ──────────────────────────────────────────────── */
  const saveStep = useCallback(async (step) => {
    if (!studentId) return;

    let newRowId = rowId;

    if (rowId) {
      await supabase
        .from('student_process')
        .update({ current_step: step, updated_at: new Date().toISOString() })
        .eq('id', rowId);
    } else {
      const { data } = await supabase
        .from('student_process')
        .insert({ student_id: studentId, current_step: step })
        .select('id')
        .single();
      if (data?.id) {
        newRowId = data.id;
        setRowId(data.id);
      }
    }

    setCurrentStep(step);
    // Keep cache in sync so the stepper shows the right step on re-navigation
    _processCache.set(studentId, { currentStep: step, rowId: newRowId });
  }, [studentId, rowId]);


  /* ── advance / revert / goToStep ──────────────────────────── */
  const advance = useCallback((maxStep) => {
    if (currentStep < maxStep) saveStep(currentStep + 1);
  }, [currentStep, saveStep]);

  const revert = useCallback(() => {
    if (currentStep > 0) saveStep(currentStep - 1);
  }, [currentStep, saveStep]);

  // Jumps directly to any step index — used by auto-revert logic
  // to return to the earliest step with a rejected document.
  const goToStep = useCallback((step) => {
    saveStep(step);
  }, [saveStep]);


  return { currentStep, processLoading, advance, revert, goToStep };
}
